import React from 'react';

import {
  tryFocusFirstElement,
  tryFocusLastElement,
  tryFocusNextElement,
  tryFocusPrevElement,
} from './movement';

/**
 * Keyboard navigation handler factory.
 *
 * Adds proper `Arrow Up`, `Arrow Down`, `Home`, and `End` keys handlers.
 * Also maintains focus loop when user navigates using arrow keys.
 */
// eslint-disable-next-line max-len
export const createKeyNavHandler = (container: React.RefObject<ParentNode>) => (event: React.KeyboardEvent<HTMLElement>) => {
  // Do nothing if the event was already prevented.
  if (event.defaultPrevented) {
    return;
  }

  // Container element.
  const containerElement = container.current;
  // Currently selected element.
  const currentElement = event.currentTarget as Element || null;

  const handlers: Record<string, () => HTMLElement | null> = {
    // Focus prev element or jump to the last element if no next element available.
    ArrowUp: () => (tryFocusPrevElement(currentElement) ?? tryFocusLastElement(containerElement)),
    // Focus next element or jump to the first element if no next element available.
    ArrowDown: () => (tryFocusNextElement(currentElement) ?? tryFocusFirstElement(containerElement)),
    // Focus first element.
    Home: () => tryFocusFirstElement(containerElement),
    // Focus last element.
    End: () => tryFocusLastElement(containerElement),
  };

  // Find and execute a corresponding keyboard event handler if any.
  const handler = handlers[event.key];
  if (!handler) {
    return;
  }

  // Prevent default event behavior if any element was focused.
  const focusedElement = handler();
  if (focusedElement) {
    event.preventDefault();
  }
};
